import { Helmet } from "react-helmet-async";

const SITE_NAME = "Vixera";
const DEFAULT_IMAGE = "/icon-512.png";

/**
 * Renders <title>, meta description + OG + Twitter + JSON-LD tags.
 *
 * Props (all optional):
 *  - title         : page title (site name appended automatically)
 *  - description   : meta description
 *  - image         : absolute or public-relative URL
 *  - url           : canonical url; defaults to current window.location.href
 *  - type          : og:type; default 'website' (use 'profile' for a companion)
 *  - jsonLd        : structured-data object (already-shaped)
 *  - noIndex       : if true, adds robots noindex
 */
export default function SEO({
  title,
  description,
  image,
  url,
  type = "website",
  jsonLd,
  noIndex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Premium Companionship Directory`;
  const desc = description || "South Africa's premium 18+ companionship directory. Verified, discreet, unforgettable.";
  const canonical = url || (typeof window !== "undefined" ? window.location.href : undefined);
  const img = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {canonical && <link rel="canonical" href={canonical} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:locale" content="en_ZA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
